import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { getApiUrl } from "../../utils/baseUrl";
import OptimizedSelect from "@shared/OptimizedSelect";
import ReactTooltip from "react-tooltip";
import commaFormat from "@utils/commaFormat";

const searchTypeCustomStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 54,
    backgroundColor: "#fff",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 600,
    border: state.isFocused ? "1px solid #6f08f1 !important" : "solid 1px #d0d5dd !important",
    boxShadow: state.isFocused ? "0 0 0 2px #e4dbfa !important" : "none !important",
  }),
  menu: (base) => ({
    ...base,
    fontSize: "14px",
    fontWeight: 600,
    padding: "8px",
  }),
  option: (base, { isDisabled, isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isDisabled ? null : isSelected ? "transparent" : isFocused ? "transparent" : null,
    color: isDisabled ? null : isSelected ? "#1a1722" : isFocused ? "#1a1722" : null,
    cursor: "pointer !important",
    marginBottom: "8px",
  }),
};

const requestContactType = {
  phoneAndEmail: "both",
  phone: "phone",
  email: "email",
};

const successMessageEnum = {
  fiftyContact: "We have got your request. Once we find the contacts, we would share the list over your email in next 24 business hours",
  hundredContact: "We have got your request. Once we find the contacts, we would share the list over your email in next 72 business hours",
};

const intelAPIEndpoints = {
  contactApolloDropdown: getApiUrl("CompanyContactDetails/contactApolloDropdown"),
  downloadBulkContact: getApiUrl("CompanyContactDetails/downloadBulkContact"),
};

const ContactModal = ({ prospectData, handleClose, show, remainingValueAddCount, currentUserPlan, type, setSingleData, onConfirm }) => {
  const [departmentsList, setDepartmentsList] = useState([]);
  const [seniorityList, setSeniorityList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [seniority, setSeniority] = useState([]);
  const [location, setLocation] = useState([]);
  const [contact, setContact] = useState(1);
  const [title, setTitle] = useState("");
  const [contactType, setContactType] = useState(requestContactType.email);
  const [step, setStep] = useState(1);
  const [data, setData] = useState(prospectData);
  const [mergedCompany, setMergedCompany] = useState([]);
  const [description, setDescription] = useState("");

  const handleDepartmentsChange = useCallback((newValue) => {
    setDepartments(newValue || []);
  }, []);

  const handleSeniorityChange = useCallback((newValue) => {
    setSeniority(newValue || []);
  }, []);

  const handleLocationChange = useCallback((newValue) => {
    setLocation(newValue || []);
  }, []);

  const handleContactChange = useCallback((e) => {
    const value = +e.target.value;
    if (value > 5) {
      setContact(5);
    } else if (value < 0) {
      setContact(1);
    } else {
      setContact(value);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchDropdownData = async () => {
      try {
        const {
          data: { message },
        } = await axios.get(intelAPIEndpoints.contactApolloDropdown);
        if (mounted) {
          setDepartmentsList(message.department);
          setSeniorityList(message.seniority);
          setLocationList(message.location.map((item) => ({ label: item, value: item })));
        }
      } catch (error) {
        toast.error("Failed to load dropdown data");
      }
    };

    fetchDropdownData();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (prospectData?.length) {
      const mergedData = prospectData.filter((item) => !!item.is_merged);
      const unMergedData = prospectData.filter((item) => !item.is_merged);
      setData(unMergedData);
      setMergedCompany(mergedData);
    }
  }, [prospectData]);

  const fetchBulkHandler = useCallback(async () => {
    const requestObject = {
      company_name: data.filter((item) => item.company_name).length ? data.map((item) => item.company_name) : data.map((item) => item.key),
      company_id: data.filter((item) => item.company_id).length ? data.map((item) => item.company_id) : type === "Importer" ? data.map((item) => item.consignee_company_id || item.hashed_id) : data.map((item) => item.shipper_company_id || item.hashed_id),
      job_title: title,
      department: departments.map((item) => item.value),
      seniority: seniority.map((item) => item.value),
      location: location.map((item) => item.value),
      user_active_plan: currentUserPlan,
      requested_contacts: contact,
      contact_type: contactType,
      description: description,
    };

    try {
      const res = await axios.post(intelAPIEndpoints.downloadBulkContact, requestObject);
      if (res.data.status === "failed") {
        toast.error(res?.data?.message || "Something went wrong! Please try again");
      } else {
        setStep(2);
      }
      if (setSingleData) {
        setSingleData({});
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  }, [data, type, title, departments, seniority, location, currentUserPlan, contact, contactType, description, setSingleData]);

  const successMessage = useMemo(() => {
    return data.length > 50 ? successMessageEnum.hundredContact : successMessageEnum.fiftyContact;
  }, [data.length]);

  const isSubmitDisabled = useMemo(() => {
    return !departments.length && !seniority.length && !location.length && !title;
  }, [departments.length, seniority.length, location.length, title]);

  return (
    <Modal backdrop="static" centered size="lg" show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title className="w-100 d-flex align-items-center justify-content-between">
          <span>
            Fetch Contacts for{" "}
            <span
              className="text-capitalize"
              data-tip={data.map((item) => item.key).join(", ")} // Concatenate all keys
              data-for="id_05">
              {data.length === 1 ? (data[0]?.key ? (data[0]?.key.length > 18 ? data[0]?.key.slice(0, 18) + "..." : data[0]?.key) : "") : data.length}
            </span>{" "}
            {data.length > 1 ? "companies" : ""}
            <ReactTooltip className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="top" id="id_05" />
          </span>

          {!!remainingValueAddCount && step === 1 && (
            <span className="credits-strip">
              <img src="https://res.cloudinary.com/ddfycftwg/image/upload/v1641874517/images/icons/Iconly_Bold_Info_Circle_bwgt7p.svg" alt="info icon" />
              <span>Remaining Value Add Credit(s): {commaFormat(remainingValueAddCount)}</span>
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 1 ? (
          <div>
            <p className="merged-info mb-3">Add values to at least one of the first four fields</p>

            <div>
              <label className="contact-label">Enter Job Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter Job title of the contacts" className="contact-input-field d-block" />
            </div>

            <div className="mt-3">
              <label className="contact-label">Select Department of the contacts</label>
              <OptimizedSelect options={departmentsList} styles={searchTypeCustomStyles} value={departments} onChange={handleDepartmentsChange} />
            </div>

            <div className="mt-3">
              <label className="contact-label">Select Management level of the contacts</label>
              <OptimizedSelect options={seniorityList} styles={searchTypeCustomStyles} value={seniority} onChange={handleSeniorityChange} />
            </div>

            <div className="mt-3">
              <label className="contact-label">Select the Country of the contacts</label>
              <OptimizedSelect options={locationList} styles={searchTypeCustomStyles} value={location} onChange={handleLocationChange} />
            </div>

            <div className="my-3">
              <label className="contact-label">Maximum number of contacts required</label>
              <div className="d-flex">
                <div onClick={() => contact > 1 && setContact(contact - 1)} className={`contact-modal-button ${contact > 1 ? "" : "disabled-button"}`}>
                  -
                </div>
                <div>
                  <input value={contact} onChange={handleContactChange} type="number" className="contact-modal-input" />
                </div>
                <div onClick={() => contact < 5 && setContact(contact + 1)} className={`contact-modal-button ${contact < 5 ? "" : "disabled-button"}`}>
                  +
                </div>
              </div>
            </div>

            <div className="mt-3 mb-4">
              <label className="contact-label">Type of contact</label>
              <div className="row justify-content-between gx-1 p-0 m-0" onChange={(e) => setContactType(e.target.value)}>
                <div className={`col-4 ${contactType === requestContactType.email ? "custom-card-fetch-active" : "custom-card-fetch"}`}>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={contactType === requestContactType.email} name="contactType" id="email" value={requestContactType.email} />
                    <label className="form-check-label" htmlFor="email">
                      Email
                    </label>
                  </div>
                </div>

                <div className={`col-4 ${contactType === requestContactType.phone ? "custom-card-fetch-active" : "custom-card-fetch"}`}>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="contactType" checked={contactType === requestContactType.phone} id="phone" value={requestContactType.phone} />
                    <label className="form-check-label" htmlFor="phone">
                      Phone Number
                    </label>
                  </div>
                </div>

                <div className={`col-4 ${contactType === requestContactType.phoneAndEmail ? "custom-card-fetch-active" : "custom-card-fetch"}`}>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="contactType" checked={contactType === requestContactType.phoneAndEmail} id="emailAndPhone" value={requestContactType.phoneAndEmail} />
                    <label className="form-check-label" htmlFor="emailAndPhone">
                      Email and Phone number
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="my-3">
              <label className="contact-label">Any message or description you want to add</label>
              <textarea className="contact-form-text-area" onChange={(e) => setDescription(e.target.value)} rows={4} maxLength="280" />
            </div>

            <div>
              <button
                disabled={isSubmitDisabled}
                className={`btn btn-primary trademo-new-btn w-100 py-3 ${isSubmitDisabled ? "fetch-contact-disable" : ""}`}
                onClick={() => {
                  onConfirm?.();
                  fetchBulkHandler();
                }}>
                Fetch Contacts
              </button>

              {mergedCompany.length > 0 && (
                <p className="text-center pt-3 mb-0 merged-info">
                  <img className="mr-1" alt="info icon" src="https://res.cloudinary.com/drzta9shk/image/upload/v1682342727/intel/icons/info_jutr6t.svg" />
                  {mergedCompany.length} Selected Merged Companies have been excluded from your search query
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="bulk_success_container mx-auto text-center mt-3 mb-3">
              <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1682666685/intel/icons/email_yy4dnq.svg" alt="email sent" />
              <p className="bulk_success_msg my-3 py-3">{successMessage}</p>
              <button onClick={handleClose} className="btn btn-outline-secondary trademo-outline-btn">
                Close
              </button>
            </div>
            <p className="mt-3 pt-3 text-center bulk_text_note">*Please note that the download link for contacts received in the email will be valid for 24 hours.</p>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default React.memo(ContactModal);
